import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useProducts } from '../../contexts';
import { Order } from '../../components/Order';
import { ErrorModal } from '../../components/ErrorModal';
import Spinner from '../../components/Spinner/Spinner';
import { Order as OrderData, OrderProduct } from '../../types/products';
import { CHARGEBEE_INVOICE } from '../../services/ApolloClient';
import { navigateWithReload } from '../../utils';

const TITLE_DEFAULT = 'Congratulations! Your order is complete.';
const DESCRIPTION_DEFAULT =
  'We are getting started on your order right away and you will receive a confirmation email shortly';

const TITLE_SERVICE = "You're all set — your order is confirmed.";
const DESCRIPTION_SERVICE = 'You’ll receive a confirmation email shortly with the next steps';

const DEFAULT_PRODUCT_PAGE_URL = '/products/embody-core-support/';

const ConfirmationPage = () => {
  const { cleanCart } = useProducts();
  const [searchParams] = useSearchParams();
  const hostedPageId = searchParams.get('id');

  const { data: invoiceData, loading } = useQuery(CHARGEBEE_INVOICE, {
    variables: { hostedPageId },
    skip: !hostedPageId,
  });

  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [title, setTitle] = useState(TITLE_DEFAULT);
  const [description, setDescription] = useState(DESCRIPTION_DEFAULT);
  const [productPageUrl, setProductPageUrl] = useState(DEFAULT_PRODUCT_PAGE_URL);
  const [showTotalPrice, setShowTotalPrice] = useState(false);
  const [isServiceOrder, setIsServiceOrder] = useState(false);

  useEffect(() => {
    cleanCart();
    if (!hostedPageId) {
      navigateWithReload(productPageUrl);
    }
  }, [cleanCart, hostedPageId, productPageUrl]);

  useEffect(() => {
    if (!invoiceData) return;

    const currentDate = new Date();
    const invoice = invoiceData?.chargebeeInvoice;
    const email: string = invoice?.shippingAddressEmail || invoice?.customerEmail || '';

    const rawProducts = invoice?.products ?? [];
    const products = rawProducts.map((p: OrderProduct) => ({
      ...p,
      image: p?.image || undefined,
      color: p?.color || '',
      size: p?.size || '',
    }));

    const isService =
      products.length > 0 && products.every((p: OrderProduct) => !p.size && !p.color);

    setIsServiceOrder(isService);

    const numberOfProducts = products.length;
    setShowTotalPrice(!(isService && numberOfProducts === 1));

    const baseTitle = isService ? TITLE_SERVICE : TITLE_DEFAULT;
    const baseDesc = isService ? DESCRIPTION_SERVICE : DESCRIPTION_DEFAULT;
    setTitle(baseTitle);
    setDescription(baseDesc + (email ? ` to ${email}.` : '.'));

    setProductPageUrl(isService ? '/' : DEFAULT_PRODUCT_PAGE_URL);

    setOrderData({
      createdAt: currentDate.toString(),
      status: 'Processing',
      discount: invoice?.totalDiscountAmount,
      total: invoice?.totalAmount,
      subTotal: invoice?.totalCostItems,
      products,
    });
  }, [loading, invoiceData]);

  if (loading) return <Spinner />;
  if (!loading && !orderData)
    return <ErrorModal onSubmit={() => navigateWithReload(productPageUrl)} />;

  return (
    <>
      <Helmet>
        <title>Order confirmation | Every Mother</title>
      </Helmet>
      <Order
        title={title}
        description={description}
        orderData={orderData}
        showTotalPrice={showTotalPrice}
        isServiceOrder={isServiceOrder}
      />
    </>
  );
};

export default ConfirmationPage;
